import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  TextField,
  Container,
  Typography,
  IconButton,
  TablePagination,
  Box,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import * as Icons from '@mui/icons-material';
import { syncDocalls } from '../services/docallsService';
import { db } from './Dexie';
import { useLiveQuery } from 'dexie-react-hooks';

// Definizione delle colonne basata su chiamate Apache tipiche
const headCells = [
  { id: 'docall', numeric: true, disablePadding: false, label: 'ID Chiamata' },
  { id: 'REMOTE_ADDR', numeric: false, disablePadding: false, label: 'Indirizzo IP' },
  { id: 'SERVER_NAME', numeric: false, disablePadding: false, label: 'URL Chiamato' },
  { id: 'date', numeric: false, disablePadding: false, label: 'Data/Ora' },
];

function DoCallsComponent() {
  const navigate = useNavigate();
  
  // Stati per la paginazione
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Stati per l'ordinamento
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('docall');
  
  // Stati per i filtri
  const [filters, setFilters] = useState({
    docall: '',
    REMOTE_ADDR: '',
    SERVER_NAME: '',
    date: '',
  });

  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

  // Ottieni i dati da Dexie
  const allDocalls = useLiveQuery(() => db.docalls.toArray()) || [];

  // Filtraggio e ordinamento lato client
  const filteredAndSortedRows = useMemo(() => {
    let result = [...allDocalls];

    // Filtraggio
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        const filterValue = filters[key].toLowerCase();
        result = result.filter(row => {
          const cellValue = String(row[key] || '').toLowerCase();
          return cellValue.includes(filterValue);
        });
      }
    });

    // Ordinamento
    result.sort((a, b) => {
      let valA = a[orderBy];
      let valB = b[orderBy];

      // Gestione specifica per numeri (ID) e date
      if (orderBy === 'docall') {
        valA = parseInt(valA) || 0;
        valB = parseInt(valB) || 0;
      } else if (orderBy === 'date') {
        valA = new Date(valA).getTime() || 0;
        valB = new Date(valB).getTime() || 0;
      } else {
        valA = String(valA || '').toLowerCase();
        valB = String(valB || '').toLowerCase();
      }

      if (valA < valB) return order === 'asc' ? -1 : 1;
      if (valA > valB) return order === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [allDocalls, filters, orderBy, order]);

  // Paginazione locale
  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredAndSortedRows.slice(start, start + rowsPerPage);
  }, [filteredAndSortedRows, page, rowsPerPage]);

  const handleSync = useCallback(async () => {
    setLoading(true);
    try {
      const response = await syncDocalls();
      if (response.success) {
        setToast({ open: true, message: `Sincronizzati ${response.count} record`, severity: 'success' });
      } else {
        setToast({ open: true, message: response.error || 'Errore durante il recupero dei dati', severity: 'error' });
      }
    } catch (error) {
      setToast({ open: true, message: 'Errore di connessione', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Sincronizza i dati al caricamento se Dexie è vuoto
    const checkAndSync = async () => {
      const count = await db.docalls.count();
      if (count === 0) {
        handleSync();
      }
    };
    checkAndSync();
  }, [handleSync]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const handleFilterChange = (event, columnId) => {
    setFilters({
      ...filters,
      [columnId]: event.target.value,
    });
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 6 }}>
      <Paper elevation={0} sx={{
        p: 2,
        mb: 4,
        background: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/')} sx={{ color: 'white', mr: 2 }}>
            <Icons.ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'white', m: 0 }}>
              _docalls Management (Client-side)
            </Typography>
            <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
              Totale record filtrati: {filteredAndSortedRows.length.toLocaleString()} di {allDocalls.length.toLocaleString()}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleSync} sx={{ color: 'white' }} disabled={loading}>
          {loading ? <CircularProgress size={24} color="inherit" /> : <Icons.Sync />}
        </IconButton>
      </Paper>

      <Paper sx={{ width: '100%', mb: 2, backgroundColor: 'rgba(255, 255, 255, 0.05)', color: 'white', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: '70vh' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    sortDirection={orderBy === headCell.id ? order : false}
                    sx={{ backgroundColor: '#1e1e1e', color: 'white' }}
                  >
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : 'asc'}
                      onClick={() => handleRequestSort(headCell.id)}
                      sx={{
                        color: 'white !important',
                        '&.Mui-active': { color: 'primary.main !important' },
                        '& .MuiTableSortLabel-icon': {
                          color: 'rgba(255, 255, 255, 0.7) !important',
                        },
                      }}
                    >
                      {headCell.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell key={`${headCell.id}-filter`} sx={{ backgroundColor: '#1e1e1e', py: 1 }}>
                    <TextField
                      variant="standard"
                      size="small"
                      fullWidth
                      placeholder={`Search...`}
                      value={filters[headCell.id]}
                      onChange={(e) => handleFilterChange(e, headCell.id)}
                      sx={{
                        '& .MuiInput-underline:before': { borderBottomColor: 'rgba(255, 255, 255, 0.2)' },
                        '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: 'primary.main' },
                        input: { color: 'white', fontSize: '0.8rem' } 
                      }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && paginatedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={headCells.length} align="center" sx={{ py: 10 }}>
                    <CircularProgress color="primary" />
                    <Typography sx={{ mt: 2 }}>Sincronizzazione dati in corso...</Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={headCells.length} align="center" sx={{ py: 10 }}>
                    <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>Nessun record trovato</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRows.map((row, index) => (
                  <TableRow key={row.docall || index} hover sx={{ '&:hover': { backgroundColor: 'rgba(255,255,255,0.03) !important' } }}>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.7)' }}>{row.docall}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{row.REMOTE_ADDR}</TableCell>
                    <TableCell sx={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.SERVER_NAME}
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>
                      {row.date ? new Date(row.date).toLocaleString() : 'N/A'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={filteredAndSortedRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            color: 'white',
            borderTop: '1px solid rgba(255,255,255,0.1)',
            '.MuiTablePagination-selectIcon': { color: 'white' },
            '.MuiTablePagination-actions': { color: 'white' }
          }}
        />
      </Paper>

      <Snackbar 
        open={toast.open} 
        autoHideDuration={6000} 
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default DoCallsComponent;
